import { CodeBlock, SectionTitle, SubTitle, InfoBox, Badge } from './shared';

export default function ArchitectureContent() {
  return (
    <div>
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-white mb-3">架构</h1>
        <p className="text-slate-400 text-base leading-relaxed">
          OpenClaw 的 Gateway 网关架构详解，包括组件、流程、连接生命周期和线路协议。
        </p>
      </div>

      <SectionTitle id="arch-gateway">Gateway 网关</SectionTitle>
      <p className="text-slate-300 text-sm leading-relaxed mb-4">
        Gateway 是 OpenClaw 的核心组件，负责统一管理所有渠道的消息路由。所有渠道都通过 Gateway 与 AI 智能体进行通信，实现消息的统一处理和分发。
      </p>
      <div className="bg-slate-800/40 border border-slate-700/50 rounded-lg p-5 my-4">
        <div className="flex items-center justify-center gap-4 flex-wrap">
          {['WhatsApp', 'Telegram', 'Discord', 'Slack'].map((ch) => (
            <div key={ch} className="flex flex-col items-center gap-1.5">
              <div className="w-10 h-10 flex items-center justify-center bg-slate-700 rounded-lg">
                <i className="ri-message-2-line text-cyan-400"></i>
              </div>
              <span className="text-xs text-slate-400">{ch}</span>
            </div>
          ))}
          <div className="flex items-center">
            <i className="ri-arrow-right-line text-slate-500 text-xl mx-2"></i>
          </div>
          <div className="flex flex-col items-center gap-1.5">
            <div className="w-14 h-14 flex items-center justify-center bg-gradient-to-br from-cyan-500/20 to-teal-500/20 border border-cyan-500/30 rounded-xl">
              <i className="ri-router-line text-cyan-400 text-2xl"></i>
            </div>
            <span className="text-xs text-cyan-400 font-medium">Gateway</span>
          </div>
          <div className="flex items-center">
            <i className="ri-arrow-right-line text-slate-500 text-xl mx-2"></i>
          </div>
          <div className="flex flex-col items-center gap-1.5">
            <div className="w-10 h-10 flex items-center justify-center bg-slate-700 rounded-lg">
              <i className="ri-robot-line text-teal-400"></i>
            </div>
            <span className="text-xs text-slate-400">AI 智能体</span>
          </div>
        </div>
      </div>

      <SectionTitle id="arch-components">组件和流程</SectionTitle>
      <p className="text-slate-300 text-sm leading-relaxed mb-3">
        Gateway 由以下核心组件构成：
      </p>
      <ul className="space-y-2.5 text-sm text-slate-300 mb-4">
        {[
          ['ri-plug-line', '渠道适配器', '负责与各消息平台的协议对接，处理平台特定的消息格式'],
          ['ri-route-line', '消息路由器', '根据配置规则将消息分发到对应的智能体'],
          ['ri-database-2-line', '会话存储', '持久化保存会话状态和消息历史'],
          ['ri-shield-check-line', '认证管理', '统一管理 OAuth 令牌和 API 密钥'],
        ].map(([icon, title, desc]) => (
          <li key={title} className="flex items-start gap-2.5">
            <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
              <i className={`${icon} text-cyan-400`}></i>
            </div>
            <span><strong className="text-white">{title}：</strong>{desc}</span>
          </li>
        ))}
      </ul>

      <SectionTitle id="arch-lifecycle">连接生命周期</SectionTitle>
      <p className="text-slate-300 text-sm leading-relaxed mb-3">
        每个渠道连接都经历以下生命周期阶段：
      </p>
      <div className="space-y-3 my-4">
        {[
          ['初始化', '加载渠道配置，建立与平台的连接'],
          ['认证', '完成 OAuth 流程或 API 密钥验证'],
          ['就绪', '开始接收和处理消息'],
          ['运行中', '持续处理消息，维护心跳连接'],
          ['重连', '连接断开时自动重试，支持指数退避'],
          ['关闭', '优雅关闭连接，保存状态'],
        ].map(([stage, desc], i) => (
          <div key={stage} className="flex items-start gap-3">
            <span className="w-6 h-6 flex items-center justify-center bg-cyan-500/10 text-cyan-400 rounded-full text-xs font-bold flex-shrink-0 mt-0.5">{i + 1}</span>
            <div>
              <span className="text-white font-medium text-sm">{stage}：</span>
              <span className="text-slate-300 text-sm">{desc}</span>
            </div>
          </div>
        ))}
      </div>

      <SectionTitle id="arch-protocol">线路协议摘要</SectionTitle>
      <p className="text-slate-300 text-sm leading-relaxed mb-3">
        Gateway 内部使用统一的消息协议，屏蔽各平台的差异：
      </p>
      <CodeBlock language="json5" code={`// 统一消息格式
{
  id: "msg_abc123",           // 消息唯一 ID
  channelType: "telegram",    // 渠道类型
  channelId: "bot_123",       // 渠道实例 ID
  peerId: "user_456",         // 对等方 ID（用户/群组）
  threadId: "thread_789",     // 线程 ID（可选）
  content: {
    type: "text",             // 内容类型：text / image / file / ...
    text: "Hello, world!",    // 文本内容
  },
  timestamp: 1700000000000,   // 时间戳（毫秒）
  metadata: {}                // 平台特定元数据
}`} />

      <SectionTitle id="arch-pairing">配对 + 本地信任</SectionTitle>
      <p className="text-slate-300 text-sm leading-relaxed mb-3">
        对于需要扫码配对的渠道（如 WhatsApp），Gateway 使用本地信任机制：
      </p>
      <ul className="space-y-2 text-sm text-slate-300">
        <li className="flex items-start gap-2.5">
          <i className="ri-checkbox-circle-line text-cyan-400 mt-0.5 flex-shrink-0"></i>
          <span>配对信息加密存储在本地工作区，重启后无需重新配对</span>
        </li>
        <li className="flex items-start gap-2.5">
          <i className="ri-checkbox-circle-line text-cyan-400 mt-0.5 flex-shrink-0"></i>
          <span>支持多账户并行，每个账户独立存储配对信息</span>
        </li>
        <li className="flex items-start gap-2.5">
          <i className="ri-checkbox-circle-line text-cyan-400 mt-0.5 flex-shrink-0"></i>
          <span>配对文件不应提交到版本控制系统，避免泄露</span>
        </li>
      </ul>

      <SectionTitle id="arch-codegen">协议类型和代码生成</SectionTitle>
      <p className="text-slate-300 text-sm leading-relaxed mb-3">
        Gateway 使用 TypeScript 类型系统确保消息格式的类型安全，并支持从配置文件自动生成类型定义。
      </p>
      <CodeBlock language="bash" code={`# 从配置生成类型定义
openclaw codegen --config openclaw.json5 --output types/`} />

      <SectionTitle id="arch-remote">远程访问</SectionTitle>
      <p className="text-slate-300 text-sm leading-relaxed mb-3">
        支持通过 Tailscale 或 SSH 隧道实现远程访问，适合将 Gateway 部署在远程服务器上：
      </p>
      <CodeBlock language="json5" code={`{
  gateway: {
    remote: {
      // 通过 Tailscale 暴露服务
      tailscale: {
        enabled: true,
        hostname: "my-openclaw",
      },
      // 或通过 SSH 隧道
      ssh: {
        host: "user@remote-server.com",
        port: 22,
        localPort: 3000,
      }
    }
  }
}`} />

      <SectionTitle id="arch-snapshot">操作快照</SectionTitle>
      <p className="text-slate-300 text-sm leading-relaxed mb-3">
        Gateway 支持操作快照功能，可以在任意时刻保存和恢复运行状态：
      </p>
      <CodeBlock language="bash" code={`# 创建快照
openclaw snapshot create --name "before-update"

# 列出快照
openclaw snapshot list

# 恢复快照
openclaw snapshot restore --name "before-update"`} />

      <SectionTitle id="arch-invariants">不变量</SectionTitle>
      <p className="text-slate-300 text-sm leading-relaxed mb-3">
        Gateway 维护以下核心不变量，确保系统的一致性和可靠性：
      </p>
      <ul className="space-y-2 text-sm text-slate-300">
        <li className="flex items-start gap-2.5">
          <i className="ri-shield-check-line text-emerald-400 mt-0.5 flex-shrink-0"></i>
          <span><strong className="text-white">消息幂等性：</strong>同一消息不会被处理两次（通过消息 ID 去重）</span>
        </li>
        <li className="flex items-start gap-2.5">
          <i className="ri-shield-check-line text-emerald-400 mt-0.5 flex-shrink-0"></i>
          <span><strong className="text-white">顺序保证：</strong>同一会话内的消息按顺序处理</span>
        </li>
        <li className="flex items-start gap-2.5">
          <i className="ri-shield-check-line text-emerald-400 mt-0.5 flex-shrink-0"></i>
          <span><strong className="text-white">状态一致性：</strong>会话状态在崩溃恢复后保持一致</span>
        </li>
        <li className="flex items-start gap-2.5">
          <i className="ri-shield-check-line text-emerald-400 mt-0.5 flex-shrink-0"></i>
          <span><strong className="text-white">单一数据源：</strong>Gateway 是所有会话状态的唯一权威来源</span>
        </li>
      </ul>
    </div>
  );
}
